import {
  useIsMutating,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import {
  JobBoardsService,
  type UpdateIndeedFeedSettings,
} from 'api-clients/monolith';

import { queryKeys } from '@/query-keys';

export const indeedSettingsMutationKey = ['indeedSettings', 'write'] as const;
export const indeedSettingsQueryOptions = {
  queryKey: queryKeys.settings.contracts.indeedSettings,
  queryFn: () => JobBoardsService.getInternalApiJobBoardsIndeedIntegration(),
};

export function useIndeedSettings() {
  return useSuspenseQuery(indeedSettingsQueryOptions);
}

function useInvalidateIndeedSettings() {
  const client = useQueryClient();
  return () => {
    const refetchType =
      client.isMutating({ mutationKey: indeedSettingsMutationKey }) > 1
        ? 'none'
        : 'active';
    void client.invalidateQueries({
      queryKey: queryKeys.settings.contracts.indeedSettings,
      refetchType,
    });
    Object.values(queryKeys.jobPosting.channels).forEach(queryKey => {
      void client.invalidateQueries({ queryKey, refetchType });
    });
    void client.invalidateQueries({
      queryKey: queryKeys.settings.channels.integrated,
      refetchType,
    });
  };
}

export function useUpdateIndeedSettings() {
  const onSettled = useInvalidateIndeedSettings();
  const client = useQueryClient();
  return useMutation({
    mutationKey: indeedSettingsMutationKey,
    onMutate: () =>
      client.cancelQueries({
        queryKey: queryKeys.settings.contracts.indeedSettings,
      }),
    mutationFn: (changed: UpdateIndeedFeedSettings) =>
      JobBoardsService.patchInternalApiJobBoardsIndeedIntegration(changed),
    onSettled,
  });
}

export function useUnlinkIndeed() {
  const onSettled = useInvalidateIndeedSettings();
  const client = useQueryClient();
  return useMutation({
    mutationKey: indeedSettingsMutationKey,
    onMutate: () =>
      client.cancelQueries({
        queryKey: queryKeys.settings.contracts.indeedSettings,
      }),
    mutationFn: () =>
      JobBoardsService.deleteInternalApiJobBoardsIndeedIntegrationUnlink(),
    onSettled,
  });
}

export function useIndeedSettingsWriting() {
  return useIsMutating({ mutationKey: indeedSettingsMutationKey }) > 0;
}
